/**
 * Seconds as h:mm:ss, or m:ss under an hour. 'N/A' when absent or zero, for
 * read-only labels where a missing measurement should say so.
 */
export function formatDuration(seconds?: number | null): string {
  if (!seconds || seconds <= 0) return 'N/A'
  return formatTimecode(seconds)
}

/**
 * Seconds as a timecode for an input: h:mm:ss, or m:ss under an hour. Empty
 * when absent, so an unset trim point shows as a blank box.
 */
export function formatTimecode(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return ''
  const total = Math.floor(seconds)
  const hrs = Math.floor(total / 3600)
  const mins = Math.floor((total % 3600) / 60)
  const secs = total % 60
  const mm = String(mins).padStart(hrs ? 2 : 1, '0')
  const ss = String(secs).padStart(2, '0')
  return hrs ? `${hrs}:${mm}:${ss}` : `${mm}:${ss}`
}

/**
 * Whole seconds from a typed timecode: "64", "1:04", "1:44:06" or "0:01:04".
 * null for a blank value, undefined when the text is not a time.
 */
export function parseTimecode(text: string): number | null | undefined {
  const trimmed = text.trim()
  if (!trimmed) return null
  if (!/^\d+(:\d{1,2}){0,2}$/.test(trimmed)) return undefined
  const parts = trimmed.split(':').map(Number)
  if (parts.slice(1).some((p) => p > 59)) return undefined
  return parts.reduce((acc, p) => acc * 60 + p, 0)
}

/** The canonical spelling of a typed timecode, or the text unchanged when it is not one. */
export function normalizeTimecode(text: string): string {
  const seconds = parseTimecode(text)
  return typeof seconds === 'number' ? formatTimecode(seconds) : text
}
